import { TODAY } from '@Components/DateRangeButton'

import { pruneGroupbyForCollection } from './collectionGroupby'
import { COLLECTION_METRICS, definitionToMetric, getMetricDefinition } from './collectionMetrics'

export const DEFAULT_COLLECTION = 'impression'

export const DEFAULT_REPORT_ID_PREFIX = 'default-'

export const DEFAULT_COLLECTION_REPORTS = {
  impression: {
    description: 'Impression Default',
    pinned: true,
    groupby: ['day'],
    metricKeys: ['total', 'revenue', 'rpi'],
    sortby: ['total desc'],
    chart: {
      name: '',
      dataType: 'line-chart',
      dimensions: ['day'],
      metricKeys: ['total'],
      seriesBy: null,
      layoutSize: 'full',
    },
  },
  session: {
    description: 'Session Default',
    pinned: false,
    groupby: ['day'],
    metricKeys: ['total', 'revenue', 'cost', 'margin'],
    sortby: ['total desc'],
    chart: {
      name: '',
      dataType: 'line-chart',
      dimensions: ['day'],
      metricKeys: ['revenue', 'cost'],
      seriesBy: null,
      layoutSize: 'full',
    },
  },
  integration: {
    description: 'Integration Default',
    pinned: false,
    groupby: ['day'],
    metricKeys: ['total', 'successCalls', 'failedCalls', 'successCallsPC'],
    sortby: ['total desc'],
    chart: {
      name: '',
      dataType: 'bar-chart',
      dimensions: ['day'],
      metricKeys: ['successCalls', 'failedCalls'],
      seriesBy: null,
      layoutSize: 'full',
    },
  },
  bucketrans: {
    description: 'Bucket Transactions Default',
    pinned: false,
    groupby: ['day'],
    metricKeys: ['total', 'credits', 'cost', 'balance'],
    sortby: ['total desc'],
    chart: {
      name: '',
      dataType: 'line-chart',
      dimensions: ['day'],
      metricKeys: ['credits', 'cost'],
      seriesBy: null,
      layoutSize: 'full',
    },
  },
}

export function getDefaultReport(collection) {
  return DEFAULT_COLLECTION_REPORTS[collection] || DEFAULT_COLLECTION_REPORTS[DEFAULT_COLLECTION]
}

export function createDefaultReportMetrics(collection) {
  const metrics = getDefaultReport(collection).metricKeys
    .map(key => getMetricDefinition(collection, key))
    .filter(Boolean)
    .map(definitionToMetric)

  if (metrics.length) return metrics

  const [fallback] = COLLECTION_METRICS[collection] || []
  return fallback ? [definitionToMetric(fallback)] : []
}

export function createDefaultReportCharts(collection, chartId = 1) {
  const { chart } = getDefaultReport(collection)
  if (!chart) return []

  const metricKeys = chart.metricKeys.filter(key => getMetricDefinition(collection, key))
  if (!metricKeys.length) return []

  return [{
    ...chart,
    id: chartId,
    metricKeys,
    dimensions: pruneGroupbyForCollection(chart.dimensions, collection),
    breakdown: metricKeys[0],
  }]
}

export function createDefaultReportConfigForCollection(collection, dates = TODAY) {
  const { groupby, sortby } = getDefaultReport(collection)

  return {
    collection,
    groupby: pruneGroupbyForCollection(groupby, collection),
    metrics: createDefaultReportMetrics(collection),
    sortby: [...sortby],
    filters: [],
    startDate: dates?.start,
    endDate: dates?.end,
  }
}

export function getDefaultReportId(collection) {
  return `${DEFAULT_REPORT_ID_PREFIX}${collection}`
}

export function isDefaultReportId(id) {
  return String(id ?? '').startsWith(DEFAULT_REPORT_ID_PREFIX)
}

export const DEFAULT_REPORT_VIEWS = Object.keys(DEFAULT_COLLECTION_REPORTS).map(collection => {
  const { description, pinned } = DEFAULT_COLLECTION_REPORTS[collection]
  const id = getDefaultReportId(collection)
  const config = createDefaultReportConfigForCollection(collection)

  return {
    id,
    description,
    isDefault: true,
    configuration: {
      collection,
      groupby: config.groupby,
      metrics: config.metrics,
      sortby: config.sortby,
      filters: [],
      filterby: {},
      conditions: [],
      charts: createDefaultReportCharts(collection),
      pinned,
    },
  }
})

export const DEFAULT_PINNED_REPORT_IDS = DEFAULT_REPORT_VIEWS
  .filter(view => view.configuration.pinned)
  .map(view => view.id)
